import { Injectable } from "@angular/core";
import { FirebaseApp, initializeApp } from "firebase/app";
import { Auth, GoogleAuthProvider, getAuth, signInWithPopup, signOut } from "firebase/auth";
import { environment } from "../../../environments/environment";

/**
 * Client-side Google sign-in for the admin login page.
 *
 * This deliberately uses the raw `firebase`/`firebase/auth` modular SDK
 * (rather than `@angular/fire`'s `provideFirebaseApp`/DI pattern used by
 * `CustomFirebaseModule`) and initializes a second, separately-named Firebase
 * app instance (`"authApp"`) pointed at `environment.webFirebaseConfig` — the
 * same Firebase project web-frontend uses for auth. This is intentionally
 * isolated from the app-wide *default* Firebase app (`environment.firebaseConfig`,
 * initialized via `CustomFirebaseModule`), which is used only for the
 * unrelated auction realtime-DB feature and must not be touched.
 */
@Injectable({
  providedIn: "root",
})
export class FirebaseAuthService {
  private readonly app: FirebaseApp;
  private readonly auth: Auth;

  constructor() {
    this.app = initializeApp(environment.webFirebaseConfig, "authApp");
    this.auth = getAuth(this.app);
  }

  /**
   * Opens the Google sign-in popup and resolves with a fresh Firebase ID
   * token (to be POSTed to `/api/v1/auth/google-login`) plus the signed-in
   * email, so the caller can show it on a rejection page without a round
   * trip. Throws if the popup is closed/cancelled or sign-in otherwise fails.
   */
  async signInWithGoogleIdToken(): Promise<{ idToken: string; email: string | null }> {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(this.auth, provider);
    const idToken = await result.user.getIdToken();
    return { idToken, email: result.user.email };
  }

  /**
   * Signs out of this Firebase app's session (does not affect the user's
   * Google account elsewhere) so a retry from the "ask an admin" page opens
   * the account picker instead of silently reusing the same account.
   */
  signOutGoogle(): Promise<void> {
    return signOut(this.auth);
  }
}
